import type { ColumnDef } from '@vnetwork/ui/data';
import { Badge } from '@vnetwork/ui/primitives';
import type { Policy } from '@/api/s3';
import { Mono } from '../s3-parts';

function formatDate(raw?: string | null): string {
  if (!raw) return '—';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? raw : d.toLocaleString();
}

/** AWS-managed built-ins carry the reserved `aws` account segment in their ARN
 * (`arn:aws:iam::aws:policy/…`); anything else is customer-managed. */
export const isAwsManaged = (arn: string): boolean => /^arn:aws:iam::aws:policy\//.test(arn);

/**
 * Columns for the policy table — name, type, attachment count, updated.
 * The row itself is clickable (opens the detail view), so there is no per-row
 * action menu; delete lives on the detail page for customer-managed policies.
 */
export function policyColumns(): ColumnDef<Policy, unknown>[] {
  return [
    {
      id: 'policy_name',
      header: 'Name',
      cell: ({ row }) => <Mono>{row.original.policy_name}</Mono>,
    },
    {
      id: 'managed',
      header: 'Type',
      size: 160,
      cell: ({ row }) => {
        const managed = isAwsManaged(row.original.arn);
        return (
          <Badge tone={managed ? 'info' : 'neutral'} appearance="subtle" size="sm">
            {managed ? 'AWS managed' : 'Customer managed'}
          </Badge>
        );
      },
    },
    {
      id: 'attachment_count',
      header: 'Attachments',
      size: 140,
      cell: ({ row }) => {
        const n = row.original.attachment_count ?? 0;
        return (
          <Badge tone={n ? 'success' : 'neutral'} appearance="subtle" size="sm">
            {n}
          </Badge>
        );
      },
    },
    {
      id: 'update_date',
      header: 'Updated',
      size: 200,
      cell: ({ row }) => <Mono>{formatDate(row.original.update_date)}</Mono>,
    },
  ];
}
